import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import PageHeader from '../components/PageHeader';
import FAQAccordion from '../components/FAQAccordion';
import { getCollection, optimizeImage } from '../lib/cms';

export default function About() {
  const [faqs, setFaqs] = useState([]);

  useEffect(() => {
    async function loadFaqs() {
      const data = await getCollection('faqs');
      setFaqs(data || []);
    }
    loadFaqs();
  }, []);

  const portraitImg = optimizeImage(
    'https://assets.auxodev.dk/assets/1cab479d-9d2b-49e7-a4f6-be9602b123ca/2dee4a-72949a8cb38c4c6caf56fddf60204a98-mv2.jpg',
    600
  );

  return (
    <div className="bg-[#0b1017] text-slate-100 min-h-screen">
      <SEO
        title="Om mig | JARCon"
        description="Mit navn er Jens Asmus Ritz Christensen. Jeg har startet JARCon ApS for at optimere kunders produktionsmaskiner og opbygge nyt udstyr."
      />

      <PageHeader
        badge="Om JARCon ApS"
        title="Om mig"
        subtitle="Mød Jens Asmus Ritz Christensen – Mekatronik diplomingeniør og smed"
        breadcrumbs={[{ label: 'Om mig' }]}
        ctaText="Kontakt mig i dag"
        ctaLink="/kontakt"
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          {/* Portrait and facts */}
          <div className="lg:col-span-4 space-y-6">
            <div className="rounded-2xl overflow-hidden bg-slate-900 border border-slate-800 shadow-2xl p-4">
              <img
                src={portraitImg}
                alt="Jens Asmus Ritz Christensen, JARCon ApS"
                className="w-full h-auto rounded-xl object-cover filter contrast-105"
              />
              <div className="pt-4 text-center">
                <h2 className="font-display font-bold text-xl text-white">
                  Jens Asmus Ritz Christensen
                </h2>
                <span className="text-xs font-open-sans text-cyan-400 mt-1 block">
                  Mekatronik Diplomingeniør &amp; Smed
                </span>
                <span className="text-xs text-slate-400 mt-0.5 block font-open-sans">
                  Årgang 1965 • Sydals
                </span>
              </div>
            </div>

            <div className="p-6 rounded-xl bg-[#0e1622] border border-slate-800 space-y-3 font-open-sans text-xs text-slate-300">
              <div className="flex justify-between py-1.5 border-b border-slate-800">
                <span className="text-slate-500">Virksomhed:</span>
                <span className="font-semibold text-white">JARCon ApS</span>
              </div>
              <div className="flex justify-between py-1.5 border-b border-slate-800">
                <span className="text-slate-500">CVR:</span>
                <span className="text-white">39150255</span>
              </div>
              <div className="flex justify-between py-1.5 border-b border-slate-800">
                <span className="text-slate-500">Uddannet smed:</span>
                <span className="text-white">1986</span>
              </div>
              <div className="flex justify-between py-1.5 border-b border-slate-800">
                <span className="text-slate-500">Ingeniør:</span>
                <span className="text-white">Mekatronik Diplomingeniør</span>
              </div>
              <div className="flex justify-between py-1.5">
                <span className="text-slate-500">Lokation:</span>
                <span className="text-white">Sydals, Danmark</span>
              </div>
            </div>
          </div>

          {/* Verbatim text from scrape */}
          <div className="lg:col-span-8 space-y-8">
            <div className="p-8 sm:p-10 rounded-2xl bg-[#0e1622] border border-slate-800 space-y-6 text-slate-300 text-base leading-relaxed">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-md bg-cyan-950/70 border border-cyan-500/40 text-cyan-400 font-open-sans text-xs uppercase tracking-wider">
                <span>Historien bag virksomheden</span>
              </div>

              <h2 className="font-display text-2xl sm:text-3xl font-bold text-white tracking-tight">
                JARCon ApS
              </h2>

              <p className="text-lg font-medium text-white">
                Mit navn er Jens Asmus Ritz Christensen.
              </p>

              <p>
                Jeg har startet JARCon ApS, fordi jeg gerne vil dele ud af mine evner og erfaringer til at optimere mine kunders produktionsmaskiner og produkter, samt konstruktion og opbygning af nyt udstyr.
              </p>

              <p>
                Jeg er nok, hvad man kalder en opfinder type, og siden jeg var barn, har jeg godt kunnet lide at finde på nye måder at gøre tingene på og skabe nye ideer.
              </p>

              <p className="p-4 rounded-lg bg-[#080d14] border-l-4 border-cyan-400 italic text-white">
                &ldquo;Jeg er meget vedholdende af natur og slipper ikke en opgave, inden den er løst til alles tilfredshed.&rdquo;
              </p>

              <p>
                Min vision med virksomheden er at fremstå som en tillidsvækkende samarbejdspartner, som bidrager med ideer og løsninger der skaber værdi for mine kunder.
              </p>

              <div className="pt-4 border-t border-slate-800/80 space-y-4">
                <h3 className="font-display font-bold text-xl text-white">
                  Min baggrund:
                </h3>
                <p>
                  Jeg er årgang 1965. Bor i Sydals sammen med min familie.
                </p>
                <p>
                  Jeg er uddannet Mekatronik diplomingeniør, men oprindeligt er jeg uddannet smed i 1986.
                </p>
                <p>
                  Herefter arbejdede jeg mange år i maskinbranchen med konstruktion, montage, indkøring, tavlebygning, programmering af PLC og robotter, samt nogle år som vedligeholder i en stor produktionsvirksomhed.
                </p>
                <p>
                  Min lange alsidige praktiske baggrund kombineret med en ingeniør uddannelse, gør mig godt rustet til de fleste opgaver.
                </p>
                <p className="font-semibold text-white">
                  Jeg har et stort netværk, som er med til at sikre en professionel løsning og hurtig levering.
                </p>

                {/* Privat hobbyprojekt - JARC Audio */}
                <div className="mt-6 p-4 rounded-xl bg-[#080d14] border border-slate-800 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-slate-900 border border-slate-700/80 text-cyan-400 flex items-center justify-center shrink-0 mt-0.5">
                      <i className="fa-solid fa-volume-high text-sm" aria-hidden="true" />
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-white">Privat hobbyprojekt: JARC Audio</h4>
                      <p className="text-xs text-slate-400 mt-0.5">
                        Ud over mit professionelle ingeniørarbejde i JARCon designer og bygger jeg hifi-højttalere i fritiden.
                      </p>
                    </div>
                  </div>
                  <a
                    href="https://d6e713fb-6411-4075-ab6a-5936b7a50d5a.sites.auxodev.dk/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-1.5 px-3.5 py-2 rounded-lg bg-slate-900 hover:bg-slate-800 text-slate-200 hover:text-white border border-slate-700 hover:border-cyan-500/50 text-xs font-medium whitespace-nowrap transition-colors"
                  >
                    <span>Besøg JARC Audio</span>
                    <i className="fa-solid fa-arrow-up-right-from-square text-[10px] text-slate-400" aria-hidden="true" />
                  </a>
                </div>
              </div>

              <div className="pt-6 border-t border-slate-800 flex flex-wrap items-center gap-4">
                <Link
                  to="/kontakt"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-cyan-500 hover:bg-cyan-600 text-white font-bold text-sm transition-all"
                >
                  <span>Kontakt mig i dag</span>
                  <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                </Link>
                <a
                  href="tel:+4522884536"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-slate-900 hover:bg-slate-800 text-slate-200 border border-slate-700 font-semibold text-sm whitespace-nowrap"
                >
                  <i className="fa-solid fa-phone text-cyan-400 text-xs" aria-hidden="true" />
                  <span className="whitespace-nowrap">+45 22 88 45 36</span>
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* FAQ Section exclusively on Om os */}
        {faqs && faqs.length > 0 && (
          <div className="mt-16 pt-12 border-t border-slate-800/80 max-w-4xl mx-auto">
            <FAQAccordion items={faqs} title="Ofte stillede spørgsmål om JARCon ApS" />
          </div>
        )}
      </div>
    </div>
  );
}
